import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Users, Plus, Trash2, Phone, Mail } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Electrician", "Plumber", "Gardener", "Cleaner", "Painter", "Carpenter", "HVAC", "Pest control", "Other"];

export default function Providers() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const blank = { name: "", category: "Electrician", phone: "", email: "", notes: "" };
  const [form, setForm] = useState(blank);

  const load = useCallback(async () => { const { data } = await api.get("/providers"); setItems(data); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      await api.post("/providers", form);
      setOpen(false); setForm(blank); toast.success("Saved"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/providers/${id}`); load(); };

  return (
    <div>
      <PageHeader
        eyebrow="Little black book"
        title="Service providers"
        subtitle="Your electrician, plumber, gardener — one tap away."
        action={
          <Button data-testid="prov-add-button" onClick={() => setOpen(true)}
            className="rounded-full bg-hearth-terracotta hover:bg-hearth-terracottaDark text-white px-6 h-11">
            <Plus size={16} className="mr-1.5" /> Add contact
          </Button>
        }
      />

      {items.length === 0 ? (
        <EmptyState icon={Users} title="No contacts yet." message="Save trusted handymen, gardeners and cleaners for quick access." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((p) => (
            <div key={p.id} data-testid={`prov-card-${p.id}`} className="warm-card p-6 fade-up">
              <div className="flex items-start justify-between">
                <div>
                  <p className="caption text-hearth-mute">{p.category}</p>
                  <h3 className="font-display text-2xl font-semibold text-hearth-ink mt-1">{p.name}</h3>
                </div>
                <button data-testid={`prov-delete-${p.id}`} onClick={() => remove(p.id)} className="p-2 rounded-full text-hearth-mute hover:text-hearth-coral hover:bg-hearth-coral/10">
                  <Trash2 size={15} />
                </button>
              </div>
              {p.notes && <p className="text-sm text-hearth-mute mt-3 leading-relaxed">{p.notes}</p>}
              <div className="mt-4 flex gap-2 flex-wrap">
                {p.phone && <a href={`tel:${p.phone}`} className="pill-btn text-sm bg-hearth-terracotta/10 text-hearth-terracotta hover:bg-hearth-terracotta/20 inline-flex items-center gap-1.5"><Phone size={13} /> {p.phone}</a>}
                {p.email && <a href={`mailto:${p.email}`} className="pill-btn text-sm border border-hearth-line bg-hearth-paper hover:bg-hearth-line/50 inline-flex items-center gap-1.5"><Mail size={13} /> Email</a>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl border-hearth-line bg-hearth-paper max-w-md">
          <DialogHeader><DialogTitle className="font-display text-3xl">New contact</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="caption text-hearth-mute">Name</Label>
              <Input data-testid="prov-form-name" className="input-warm mt-1.5 h-11" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Rajesh — Bright Sparks" />
            </div>
            <div>
              <Label className="caption text-hearth-mute">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="input-warm mt-1.5 h-11 rounded-2xl"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="caption text-hearth-mute">Phone</Label>
                <Input data-testid="prov-form-phone" className="input-warm mt-1.5 h-11" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+61 4XX XXX XXX" />
              </div>
              <div>
                <Label className="caption text-hearth-mute">Email</Label>
                <Input className="input-warm mt-1.5 h-11" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="caption text-hearth-mute">Notes</Label>
              <Textarea className="input-warm mt-1.5" rows={3} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="prov-form-save" onClick={save} className="rounded-full bg-hearth-terracotta hover:bg-hearth-terracottaDark text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
